import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';

export interface ReceiptLine {
  sku: string;
  qtyExpected: number;
  unitTags: number;
  cartons: Array<{ pcsPerCarton: number; cartonTags: number }>;
  /** Not RFID-tagged: recorded by hand, never pushed to SmartLink. */
  isExcluded?: boolean;
  declaredQty?: number | null;
  keyedPieces: number;
  balance: number;
}

export interface ReceiptSummary {
  id: number;
  /** Detail endpoint only: a sub PO already holds the balance. */
  hasChild?: boolean;
  poNo: string;
  receiptNo: string;
  seq: number;
  status: 'draft' | 'submitted' | 'receiving' | 'completed' | 'cancelled';
  overKeyed: boolean;
  overKeyedNote?: string | null;
  lines: ReceiptLine[];
}

const sum = (lines: ReceiptLine[], f: (l: ReceiptLine) => number): number =>
  lines.reduce((t, l) => t + f(l), 0);

export function ReceivingListPage() {
  const q = useQuery({
    queryKey: ['receipts'],
    queryFn: () => apiFetch<ReceiptSummary[]>('/api/dashboard/receipts'),
  });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  // Offered statuses come from the data, so the filter can never advertise one
  // that isn't there — the endpoint returns only draft/submitted/receiving today.
  const statuses = useMemo(
    () => [...new Set((q.data ?? []).map((r) => r.status))].sort(),
    [q.data],
  );

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (q.data ?? []).filter((r) => {
      if (status && r.status !== status) return false;
      if (!needle) return true;
      // A sub-PO's receipt number differs from its PO, so match either.
      return (
        r.receiptNo.toLowerCase().includes(needle) || r.poNo.toLowerCase().includes(needle)
      );
    });
  }, [q.data, search, status]);

  const filtering = Boolean(search.trim() || status);

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Receiving</h1>
        <span className="main-subtitle">
          {q.data
            ? filtering
              ? `${rows.length} of ${q.data.length} open`
              : `${q.data.length} open`
            : ''}
        </span>
      </header>
      {q.data && q.data.length > 0 && (
        <div className="toolbar">
          <input
            placeholder="Search PO or receipt no…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {filtering && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
      {q.error && <div className="error">{(q.error as Error).message}</div>}
      {q.isLoading && <div className="loading">Loading…</div>}
      {q.data && q.data.length === 0 && (
        <p className="empty">
          No receipts awaiting keying. One appears here when a purchase order&apos;s lines sync
          from OBM.
        </p>
      )}
      {q.data && q.data.length > 0 && rows.length === 0 && (
        <p className="empty">No receipt matches that filter.</p>
      )}
      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Receipt</th>
              <th>PO</th>
              <th>Kind</th>
              <th>Status</th>
              <th className="num">SKUs</th>
              <th className="num">Expected</th>
              <th className="num">Keyed</th>
              <th className="num">Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.receiptNo}>
                <td className="mono">
                  <Link to={`/receiving/${encodeURIComponent(r.receiptNo)}`}>{r.receiptNo}</Link>
                </td>
                <td className="mono">
                  <Link to={`/purchase-orders/${encodeURIComponent(r.poNo)}`}>{r.poNo}</Link>
                </td>
                <td>{r.seq > 1 ? `Sub PO (${r.seq})` : 'First receipt'}</td>
                <td>
                  <span className={`badge ${r.status === 'draft' ? 'badge-pending' : 'badge-ok'}`}>{r.status}</span>
                  {r.overKeyed && <span className="badge badge-pending"> over-receipt</span>}
                </td>
                <td className="num">{r.lines.length}</td>
                <td className="num">{sum(r.lines, (l) => l.qtyExpected)}</td>
                <td className="num">{sum(r.lines, (l) => l.keyedPieces)}</td>
                <td className="num">{sum(r.lines, (l) => l.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
